import { useState } from "react";

import type { MuscleShare, WeekPoint } from "../lib/api";
import { haptic } from "../lib/telegram";

/**
 * Крупные группы для многоугольника.
 *
 * Тринадцать мышечных групп на шести сантиметрах экрана превращаются в кашу из
 * подписей. Сворачиваем в шесть осей — те самые, которыми человек и думает про
 * тренировки: грудь, спина, плечи, руки, ноги, пресс.
 */
const COARSE: { key: string; label: string; parts: string[] }[] = [
  { key: "chest", label: "Грудь", parts: ["chest"] },
  { key: "back", label: "Спина", parts: ["back", "lats", "traps"] },
  { key: "delts", label: "Плечи", parts: ["delts"] },
  { key: "arms", label: "Руки", parts: ["biceps", "triceps", "forearms"] },
  { key: "legs", label: "Ноги", parts: ["quads", "hamstrings", "glutes", "calves"] },
  { key: "abs", label: "Пресс", parts: ["abs"] },
];

export interface Axis {
  label: string;
  share: number;
  previousShare: number;
  sets: number;
}

export function coarseAxes(muscles: MuscleShare[]): Axis[] {
  const by = new Map(muscles.map((m) => [m.muscle, m]));
  return COARSE.map((group) => {
    let share = 0;
    let previousShare = 0;
    let sets = 0;
    for (const part of group.parts) {
      const row = by.get(part);
      if (!row) continue;
      share += row.share;
      previousShare += row.previous_share;
      sets += row.sets;
    }
    return { label: group.label, share, previousShare, sets };
  });
}

/**
 * Одна тренировка против обычной картины (§14, идея с главной).
 *
 * `counts` — подходы этой тренировки по мышце, `usual` — та же сводка, что и
 * в профиле (`/stats/summary`). Значения совместимы по смыслу: обе — доля
 * подходов, посчитанная одинаково, так что накладывать их на одном
 * многоугольнике честно.
 */
export function sessionAxes(counts: Record<string, number>, usual: MuscleShare[]): Axis[] {
  const usualAxes = coarseAxes(usual);
  const total = Object.values(counts).reduce((a, b) => a + b, 0);
  return COARSE.map((group, i) => {
    const sets = group.parts.reduce((sum, part) => sum + (counts[part] ?? 0), 0);
    return {
      label: group.label,
      share: total ? sets / total : 0,
      previousShare: usualAxes[i].share,
      sets,
    };
  });
}

const R = 74;
const CX = 130;
const CY = 104;
const pct = (v: number) => `${Math.round(v * 100)}%`;

/**
 * Распределение работы по группам мышц.
 *
 * Показывает ДОЛЮ ОБЪЁМА, а не силу. Сказать «грудь сильнее ног» приложение не
 * может: для этого нужен вес тела и внешние нормативы, которых у нас нет.
 * А «на ноги уходит вдвое меньше подходов, чем на руки» — считается точно и
 * отвечает на «что отстаёт» в смысле «недотренировано».
 */
interface MuscleRadarProps {
  axes: Axis[];
  title?: string;
  /** Легенда заливки и подпись при пустом тапе — например «эта тренировка». */
  currentLabel?: string;
  /** Легенда контура и хвост подписи «· было …» — например «обычно у тебя». */
  previousLabel?: string;
  previousInline?: string;
}

export function MuscleRadar({
  axes,
  title = "Куда уходит работа",
  currentLabel = "сейчас",
  previousLabel = "прошлый период",
  previousInline = "было",
}: MuscleRadarProps) {
  const [active, setActive] = useState<number | null>(null);

  const peak = Math.max(0.01, ...axes.map((a) => Math.max(a.share, a.previousShare)));
  // Шкала до круглого значения: «до 40%» читается, «до 37.4%» — нет.
  const top = Math.min(1, Math.ceil(peak * 10) / 10);

  const point = (i: number, value: number) => {
    const angle = (-90 + (360 * i) / axes.length) * (Math.PI / 180);
    const r = R * Math.min(1, value / top);
    return [CX + r * Math.cos(angle), CY + r * Math.sin(angle)] as const;
  };

  const ring = (fraction: number) =>
    axes.map((_, i) => point(i, top * fraction).join(",")).join(" ");

  const polygon = (get: (a: Axis) => number) =>
    axes.map((a, i) => point(i, get(a)).join(",")).join(" ");

  const empty = axes.every((a) => a.sets === 0);

  return (
    <figure className="radar">
      <figcaption className="chart__title">
        {title}
        <span className="chart__range">доля подходов, до {pct(top)}</span>
      </figcaption>

      {empty ? (
        <p className="lead" style={{ margin: "8px 0 0" }}>
          Пока не из чего считать: проведите пару тренировок.
        </p>
      ) : (
        <>
          <svg className="radar__svg" viewBox="0 0 260 208" role="img"
               aria-label={`Доля подходов по группам: ${axes
                 .map((a) => `${a.label} ${pct(a.share)}`)
                 .join(", ")}`}>
            {/* Кольца сетки: без них многоугольник читается как форма, а не
                как величины. */}
            {[0.5, 1].map((f) => (
              <polygon key={f} className="radar__grid" points={ring(f)} />
            ))}
            {axes.map((_, i) => {
              const [x, y] = point(i, top);
              return <line key={i} className="radar__spoke" x1={CX} y1={CY} x2={x} y2={y} />;
            })}

            {/* Предыдущий отрезок — контуром: он опора для сравнения, а не
                второй герой. */}
            <polygon className="radar__previous" points={polygon((a) => a.previousShare)} />
            <polygon className="radar__current" points={polygon((a) => a.share)} />

            {axes.map((a, i) => {
              const [x, y] = point(i, top * 1.32);
              return (
                <text
                  key={a.label}
                  className={`radar__label ${active === i ? "radar__label--active" : ""}`}
                  x={x}
                  y={y}
                  textAnchor={x < CX - 6 ? "end" : x > CX + 6 ? "start" : "middle"}
                  dominantBaseline="middle"
                  onPointerDown={() => {
                    haptic("light");
                    setActive(i);
                  }}
                >
                  {a.label}
                </text>
              );
            })}
          </svg>

          <div className="radar__foot">
            {active === null ? (
              <span className="radar__hint">Нажмите на группу, чтобы увидеть цифры</span>
            ) : (
              <span>
                <b>{axes[active].label}</b> · {pct(axes[active].share)} подходов
                {axes[active].previousShare > 0 &&
                  ` · ${previousInline} ${pct(axes[active].previousShare)}`}
              </span>
            )}
          </div>

          {/* Серий две — легенда обязательна: различать их только по цвету нельзя. */}
          <div className="radar__legend">
            <span className="legend__item">
              <span className="legend__swatch legend__swatch--current" />
              {currentLabel}
            </span>
            <span className="legend__item">
              <span className="legend__swatch legend__swatch--previous" />
              {previousLabel}
            </span>
          </div>
        </>
      )}
    </figure>
  );
}

/** Тренировок по неделям — главный график удержания: ходит человек или нет. */
export function WeeklyBars({ weeks }: { weeks: WeekPoint[] }) {
  const [active, setActive] = useState<number | null>(null);
  const peak = Math.max(1, ...weeks.map((w) => w.workouts));
  const shown = active ?? weeks.length - 1;
  const week = weeks[shown];

  const label = (iso: string) =>
    new Intl.DateTimeFormat("ru-RU", { day: "numeric", month: "short" }).format(
      new Date(iso),
    );

  return (
    <figure className="chart">
      <figcaption className="chart__title">
        Тренировок по неделям
        <span className="chart__range">до {peak} в неделю</span>
      </figcaption>

      <div className="bars" role="img"
           aria-label={`Тренировок по неделям, максимум ${peak}`}>
        {weeks.map((w, i) => (
          <button
            key={w.start}
            className={`bar ${i === shown ? "bar--active" : ""}`}
            aria-label={`Неделя ${label(w.start)}: ${w.workouts}`}
            onClick={() => {
              haptic("light");
              setActive(i);
            }}
          >
            {/* Нулевая неделя — тоже факт: рисуем её засечкой, а не пустотой. */}
            <span
              className="bar__fill"
              style={{ height: `${Math.max(3, (w.workouts / peak) * 100)}%` }}
            />
          </button>
        ))}
      </div>

      <div className="chart__foot">
        <span>{label(weeks[0]?.start ?? new Date().toISOString())}</span>
        <span className="chart__readout">
          {label(week?.start ?? new Date().toISOString())} · {week?.workouts ?? 0}
        </span>
        <span>{label(weeks[weeks.length - 1]?.start ?? new Date().toISOString())}</span>
      </div>
    </figure>
  );
}
